import {clearFieldValue, type IssueFieldValueType} from '@github-ui/issue-fields-shared/issue-field'
import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceSelectorProxy} from 'relay-runtime'

import type {
  DeleteIssueFieldValueInput,
  deleteIssueFieldValueMutation,
} from './__generated__/deleteIssueFieldValueMutation.graphql'
import {ISSUE_FIELD_VALUES_PAGE_SIZE} from './constants'
import {removeIssueFieldValue} from './helpers'

export function commitDeleteIssueFieldValueMutation({
  environment,
  input,
  fieldName,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: DeleteIssueFieldValueInput
  fieldName?: string
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  const publication = publishOptimisticFields(
    fieldName ? removeIssueFieldValue(input.issueId, input.fieldId, fieldName) : undefined,
  )
  try {
    return commitMutation<deleteIssueFieldValueMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation deleteIssueFieldValueMutation($input: DeleteIssueFieldValueInput!) {
          deleteIssueFieldValue(input: $input) {
            success
            issue {
              id
            }
          }
        }
      `,
      variables: {
        input,
      },
      onCompleted: response => {
        if (fieldName && response.deleteIssueFieldValue?.success) {
          publication.completed(removeIssueFieldValue(input.issueId, input.fieldId, fieldName))
        } else {
          publication.failed(new Error('Failed to publish deleted issue field value'))
        }
        onCompleted?.()
      },
      onError: error => {
        publication.failed(error)
        onError?.(error)
      },
      onUnsubscribe: () => publication.failed(new Error('Deleting issue field value was cancelled')),
      optimisticUpdater: store => {
        const issue = store.get(input.issueId)
        if (issue) {
          handleFieldClear(issue, input.fieldId)
        }
      },
      updater: store => {
        const issue = store.get(input.issueId)
        if (issue) {
          handleFieldClear(issue, input.fieldId)
        }
      },
    })
  } catch (error) {
    publication.failed(error instanceof Error ? error : new Error('Failed to delete issue field value'))
    throw error
  }
}

// Helper to handle field clearing logic
const handleFieldClear = (issue: ReturnType<RecordSourceSelectorProxy['get']>, fieldId: string) => {
  if (!issue) {
    return
  }
  const fieldValues = issue.getLinkedRecord('issueFieldValues', {first: ISSUE_FIELD_VALUES_PAGE_SIZE})
  if (!fieldValues) {
    return
  }

  const nodes = fieldValues.getLinkedRecords('nodes') || []
  const clearedNode = nodes.find(node => node?.getLinkedRecord('field')?.getDataID() === fieldId)
  if (!clearedNode) {
    return
  }

  const type = clearedNode.getType() as IssueFieldValueType

  clearFieldValue(clearedNode, type)
}
